import {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import PlacementTabs from '../components/creativeAssets/PlacementTabs'
import CreativeCanvas from '../components/creativeAssets/CreativeCanvas'
import LayoutTemplatePicker from '../components/creativeAssets/LayoutTemplatePicker'
import BackgroundPanel from '../components/creativeAssets/BackgroundPanel'
import ContentPanel from '../components/creativeAssets/ContentPanel'
import TextPanel from '../components/creativeAssets/TextPanel'
import AnimationPanel from '../components/creativeAssets/AnimationPanel'
import CreativeDownloadPanel from '../components/creativeAssets/CreativeDownloadPanel'
import CreativeVideoPanel from '../components/creativeAssets/CreativeVideoPanel'
import Seo from '../components/seo/Seo'
import {SITE_URL} from '../components/seo/seoConstants'
import {CtaSection} from '../components/seo/seoSections'
import {trackEvent} from '../../common/analytics/trackEvent'
import {reportError} from '../../common/errors/reportError'
import {loadSampleAssets, sampleAssetsPatch} from '../../common/utils/creativeSampleAssets'
import {
  DEFAULT_CREATIVE_GRADIENT,
  SLIDESHOW_TEMPLATES,
  VIDEO_DEFAULT_DURATION_SEC,
  VIDEO_PLACEMENT_IDS,
  type CreativePlacementId,
  type CreativeTemplateId,
} from '../../common/constants/creativeAssetSpecs'
import type {CreativeAssetState, CreativeStatePatch} from '../../common/utils/creativeAssetRenderer'
import type {CreativeVideoSettings} from '../../common/utils/creativeVideoRenderer'
import '../components/seo/SeoPage.css'
import '../components/converterShared/ConverterShared.css'
import './CreativeAssetsPage.css'

const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Ecranify App Store Creative Assets Generator',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web browser',
  url: `${SITE_URL}/app-store-creative-assets`,
  description:
    'Free browser-based generator for the iOS 27 App Store Header, Search Results and Universal creative assets — as exact-size images or looping video.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
}

const INITIAL_STATE: CreativeAssetState = {
  template: 'fan',
  bgMode: 'gradient',
  bgColor: '#2b1b6b',
  bgFrom: DEFAULT_CREATIVE_GRADIENT.from,
  bgTo: DEFAULT_CREATIVE_GRADIENT.to,
  bgAngle: DEFAULT_CREATIVE_GRADIENT.angle,
  bgImage: null,
  keyArt: null,
  keyArtZoom: 1,
  keyArtOffsetX: 0,
  keyArtOffsetY: 0,
  screenshots: [],
  icon: null,
  headline: '',
  subline: '',
  textColor: '#ffffff',
  textAlign: 'auto',
  textVertical: 'auto',
  headlineScale: 1,
  sublineScale: 1,
  headlineWeight: 'bold',
}

type CreativeMode = 'image' | 'video'

const MODES: CreativeMode[] = ['image', 'video']

const INITIAL_VIDEO: CreativeVideoSettings = {
  bg: {type: 'pan', direction: 'horizontal', speed: 1},
  content: {type: 'float', direction: 'vertical', speed: 1},
  durationSec: VIDEO_DEFAULT_DURATION_SEC,
  fps: 30,
}

const CreativeAssetsPage = () => {
  const {t} = useTranslation()
  const [state, setState] = useState<CreativeAssetState>(INITIAL_STATE)
  const [placement, setPlacement] = useState<CreativePlacementId>('header')
  const [showSafeArea, setShowSafeArea] = useState(true)
  const [mode, setMode] = useState<CreativeMode>('image')
  const [video, setVideo] = useState<CreativeVideoSettings>(INITIAL_VIDEO)

  const videoPreview = useMemo(
    () =>
      mode === 'video'
        ? {animation: {bg: video.bg, content: video.content}, durationSec: video.durationSec}
        : undefined,
    [mode, video.bg, video.content, video.durationSec],
  )

  useEffect(() => {
    trackEvent('creative_assets_viewed')
  }, [])

  // Start from ready-made sample assets so the page is never empty; replace them with your own.
  useEffect(() => {
    let cancelled = false
    loadSampleAssets()
      .then((samples) => {
        if (!cancelled) setState((prev) => ({...prev, ...sampleAssetsPatch(prev, INITIAL_STATE, samples)}))
      })
      .catch((err: unknown) => reportError(err, 'creative-assets-samples'))
    return () => {
      cancelled = true
    }
  }, [])

  const handleChange = useCallback((patch: CreativeStatePatch) => {
    setState((prev) => ({...prev, ...(typeof patch === 'function' ? patch(prev) : patch)}))
  }, [])

  const handlePlacementChange = useCallback((next: CreativePlacementId) => {
    setPlacement(next)
    trackEvent('creative_assets_placement_changed', {placement: next})
  }, [])

  const handleModeChange = useCallback((next: CreativeMode) => {
    setMode(next)
    // Universal has no video variant
    if (next === 'video') setPlacement((prev) => (VIDEO_PLACEMENT_IDS.includes(prev) ? prev : 'header'))
    trackEvent('creative_assets_mode_changed', {mode: next})
  }, [])

  const handleVideoChange = useCallback((patch: Partial<CreativeVideoSettings>) => {
    setVideo((prev) => ({...prev, ...patch}))
    if (patch.bg)
      trackEvent('creative_assets_animation_changed', {
        target: 'background',
        type: patch.bg.type,
        direction: patch.bg.direction,
        speed: patch.bg.speed,
      })
    if (patch.content)
      trackEvent('creative_assets_animation_changed', {
        target: 'content',
        type: patch.content.type,
        direction: patch.content.direction,
        speed: patch.content.speed,
      })
    if (patch.durationSec !== undefined || patch.fps !== undefined)
      trackEvent('creative_assets_video_timing_changed', {duration_sec: patch.durationSec, fps: patch.fps})
  }, [])

  const handleTemplateChange = useCallback(
    (template: CreativeTemplateId) => {
      handleChange({template})
      // Scrolling only exists for the strip (switch to it there), slideshow only for screenshot layouts; else float.
      setVideo((prev) => {
        const {type, speed} = prev.content
        if (template === 'strip' && type !== 'scroll' && type !== 'slideshow')
          return {...prev, content: {type: 'scroll', direction: 'left', speed}}
        const unsupported =
          (type === 'scroll' && template !== 'strip') ||
          (type === 'slideshow' && !SLIDESHOW_TEMPLATES.includes(template))
        if (unsupported) return {...prev, content: {type: 'float', direction: 'vertical', speed}}
        return prev
      })
      trackEvent('creative_assets_template_changed', {template})
    },
    [handleChange],
  )

  const handleSafeAreaToggle = useCallback(() => {
    setShowSafeArea((prev) => {
      trackEvent('creative_assets_safe_area_toggled', {enabled: !prev})
      return !prev
    })
  }, [])

  return (
    <div className="caa-root seo-root">
      <Seo
        title="Free App Store Header, Search Results & Video Asset Maker | Ecranify"
        description="Design the iOS 27 App Store Header, Search Results and Universal creative assets as images or looping video, free in your browser. Exact sizes, ready to upload."
        canonicalPath="/app-store-creative-assets"
        jsonLd={[JSON_LD]}
      />
      <div className="caa-grid" aria-hidden />

      <main className="caa-page">
        <header className="caa-hero">
          <span className="caa-eyebrow">
            <span className="caa-eyebrow-dot" />
            {t('creativeAssets.hero.eyebrow')}
          </span>
          <h1 className="caa-title">
            {t('creativeAssets.hero.titlePrefix')} <em>{t('creativeAssets.hero.titleEm')}</em>
          </h1>
          <p className="caa-lede">{t('creativeAssets.hero.subtitle')}</p>
        </header>

        <div className="caa-layout">
          <div className="caa-stack">
            <section className="caa-col">
              <BackgroundPanel state={state} onChange={handleChange} />
            </section>
            <section className="caa-col">
              <ContentPanel state={state} onChange={handleChange} />
            </section>
            <section className="caa-col">
              <TextPanel state={state} onChange={handleChange} />
            </section>
          </div>

          <div className="caa-center">
            <section className="caa-col caa-col-preview">
              <div className="caa-preview-sticky">
                <div className="caa-tabs caa-tabs-compact" role="tablist">
                  {MODES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      role="tab"
                      aria-selected={mode === m}
                      className={`caa-tab${mode === m ? ' is-active' : ''}`}
                      onClick={() => handleModeChange(m)}
                    >
                      {t(`creativeAssets.mode.${m}`)}
                    </button>
                  ))}
                </div>
                <PlacementTabs
                  value={placement}
                  onChange={handlePlacementChange}
                  allowed={mode === 'video' ? VIDEO_PLACEMENT_IDS : undefined}
                />
                <CreativeCanvas
                  state={state}
                  placement={placement}
                  showSafeArea={showSafeArea}
                  video={videoPreview}
                  onChange={handleChange}
                />
              </div>
              <label className="caa-toggle">
                <input type="checkbox" checked={showSafeArea} onChange={handleSafeAreaToggle} />
                <span>{t('creativeAssets.canvas.showSafeArea')}</span>
              </label>
              <p className="cvt-hint">{t('creativeAssets.canvas.dropTip')}</p>
              <p className="cvt-hint">{t('creativeAssets.canvas.safeAreaNote')}</p>
            </section>

            {mode === 'video' && (
              <section className="caa-col">
                <AnimationPanel
                  settings={video}
                  bgMode={state.bgMode}
                  template={state.template}
                  onChange={handleVideoChange}
                />
              </section>
            )}

            <section className="caa-col">
              <h2 className="caa-panel-title">{t('creativeAssets.sections.template')}</h2>
              <LayoutTemplatePicker value={state.template} onChange={handleTemplateChange} />
            </section>
          </div>

          <section className="caa-col caa-col-export">
            <h2 className="caa-panel-title">{t('creativeAssets.sections.export')}</h2>
            {mode === 'image' ? (
              <CreativeDownloadPanel state={state} />
            ) : (
              <CreativeVideoPanel state={state} placement={placement} settings={video} />
            )}
          </section>
        </div>

        <section className="caa-seo">
          <h2 className="caa-seo-title">{t('creativeAssets.seo.title')}</h2>
          <p className="caa-seo-body">{t('creativeAssets.seo.body1')}</p>
          <p className="caa-seo-body">{t('creativeAssets.seo.body2')}</p>
        </section>

        <CtaSection cta={{title: t('creativeAssets.cta.title'), body: t('creativeAssets.cta.body')}} />
      </main>
    </div>
  )
}

export default CreativeAssetsPage
